import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

// Credits are an abstraction the buyer did not ask for, so the page leads with
// what they buy — photographs — and keeps the number as the small print. A plan
// and a pack are shown side by side rather than on separate pages, because the
// only reason to choose between them is the price per photo, and that
// comparison is the decision.

function money(n) {
  return `$${Number(n).toFixed(Number(n) % 1 ? 2 : 0)}`;
}

function Card({ row, onBuy, busy, plan }) {
  const isPlan = row.kind === 'plan';
  const current = plan === row.key;
  return (
    <div
      data-testid={`buy-${row.key}`}
      className={`flex flex-col rounded-2xl border bg-[#141417] p-6 ${
        current ? 'border-node-model' : 'border-[#2A2A30]'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif-display text-xl text-white">{row.label}</h3>
        {current && (
          <span className="rounded-full bg-node-model/15 px-2 py-0.5 text-[11px] text-node-model">
            Your plan
          </span>
        )}
      </div>

      <p className="mt-4 font-serif-display text-4xl text-white">
        {money(row.price)}
        {isPlan && <span className="text-base text-white/40"> /month</span>}
      </p>

      {/* The headline is photographs. Credits are the mechanism, and the
          mechanism goes underneath in the size it deserves. */}
      <p className="mt-3 text-sm text-white/80">
        About <strong className="text-white">{row.premium_photos}</strong> premium photos
      </p>
      <p className="text-sm text-white/55">
        or {row.standard_photos} at standard quality
      </p>
      <p className="mt-1 text-[11px] text-white/35">
        {row.credits.toLocaleString()} credits
        {isPlan ? ', refilled every month' : ', yours until you spend them'}
      </p>

      <ul className="mt-5 flex flex-1 flex-col gap-2">
        {(isPlan
          ? ['Refills automatically', 'Cancel any time', 'Commercial rights included']
          : ['No expiry', 'No subscription', 'Commercial rights included']
        ).map((t) => (
          <li key={t} className="flex items-start gap-2 text-[13px] text-white/65">
            <Check size={13} className="mt-0.5 shrink-0 text-node-environment" />
            {t}
          </li>
        ))}
      </ul>

      {/* `available` dice si hay checkout puesto. Antes no venia, asi que el
          boton se ofrecia igual y el callejon sin salida se descubria despues
          de pulsarlo. Decirlo antes cuesta una linea y ahorra el desconcierto. */}
      <button
        onClick={() => onBuy(row)}
        disabled={busy || current || row.available === false}
        data-testid={`buy-btn-${row.key}`}
        className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-40"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : null}
        {current ? 'Current plan'
          : row.available === false ? 'Not on sale yet'
          : isPlan ? 'Subscribe' : 'Buy credits'}
      </button>
      {row.available === false && !current && (
        <p className="mt-2 text-center text-[11px] text-white/35">
          Bring your own API key below and shoot without buying credits at all.
        </p>
      )}
    </div>
  );
}

export default function Credits() {
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [state, setState] = useState({ balance: 0, plan: 'free' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, mine] = await Promise.all([api.creditCatalogue(), api.getCredits()]);
        setCat(c);
        setState(mine);
      } catch (e) {
        toast({ title: 'Could not load the plans', description: e.message });
      }
    })();
  }, []);

  // Checkout lives with the payment processor, not here. Sending somebody to a
  // page we host that then redirects would add a step and a place to fail; the
  // buy link is configured per product in Admin so it can point at whichever
  // processor is selling that SKU this week.
  const buy = async (row) => {
    setBusy(true);
    try {
      const { url } = await api.buyLink(row.key);
      if (!url) {
        toast({
          title: 'That one is not on sale yet',
          description: 'No checkout link is configured for this product.',
        });
        return;
      }
      window.location.href = url;
    } catch (e) {
      toast({ title: 'Could not open checkout', description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="font-serif-display text-3xl">Buy photographs, not tokens</h1>
          <span className="text-sm text-white/50" data-testid="credit-balance">
            {state.balance?.toLocaleString?.() ?? state.balance} left
          </span>
        </div>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
          A plan refills every month and works out cheaper per photo. A pack is bought once and
          never expires. Either way the images are yours to sell, on every tier.
        </p>
        {/* The one sentence that makes the rest of the page checkable in your
            head. Without it, "3,200 credits" is a number you have to trust. */}
        <p className="mt-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[#2A2A30] bg-[#141417] px-3 py-2 text-[13px] text-white/70">
          <span className="text-white">One credit is one cent.</span>
          <span className="text-white/45">
            A draft is 8, a standard is 12, a premium is 32 — so 8¢, 12¢ and 32¢ a photograph.
          </span>
        </p>

        {!cat && (
          <div className="mt-10 flex items-center gap-2 text-sm text-white/40">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {cat && (
          <>
            <h2 className="mt-10 text-sm font-semibold uppercase tracking-wider text-white/50">
              Monthly plans
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cat.plans.map((r) => (
                <Card key={r.key} row={r} onBuy={buy} busy={busy} plan={state.plan} />
              ))}
            </div>

            <h2 className="mt-12 text-sm font-semibold uppercase tracking-wider text-white/50">
              One-off packs
            </h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cat.packs.map((r) => (
                <Card key={r.key} row={r} onBuy={buy} busy={busy} plan={state.plan} />
              ))}
            </div>

            <div className="mt-12 rounded-xl border border-[#2A2A30] bg-[#141417] p-5">
              <h2 className="font-serif-display text-lg">Or bring your own key</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/65">
                Connect your own provider account and you skip credits entirely — generation is
                billed to you at the provider&apos;s own rate, which is a couple of cents a
                photograph, and nothing here meters you.
              </p>
              <button
                onClick={() => navigate('/app/keys')}
                data-testid="credits-byok"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/85 hover:border-white/40"
              >
                Set up a key <ExternalLink size={13} />
              </button>
            </div>

            <p className="mt-8 text-xs leading-relaxed text-white/35">
              What each photo costs in credits: draft {cat.costs.draft}, standard{' '}
              {cat.costs.standard}, premium {cat.costs.premium}, upscale {cat.costs.upscale}. A
              failed generation is refunded automatically.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
